import { authenticatedFetch } from '../utils.js';

class AuthService {
    constructor() {
        this.token = localStorage.getItem('token');
        this.user = JSON.parse(localStorage.getItem('user'));
    }

    async login(email, password) {
        try {
            const response = await fetch('/token', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded'
                },
                body: new URLSearchParams({
                    'username': email,
                    'password': password
                }).toString()
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Login failed');
            }

            const data = await response.json();
            this.token = data.access_token;
            this.user = data.user;

            localStorage.setItem('token', this.token);
            localStorage.setItem('user', JSON.stringify(this.user));

            return this.user;
        } catch (error) {
            throw new Error('Login failed: ' + error.message);
        }
    }

    async initiateRegistration(email, username, phone) {
        try {
            const response = await fetch('/api/users/register/initiate', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email, username, phone })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Registration initiation failed');
            }

            return await response.json();
        } catch (error) {
            throw new Error('Registration initiation failed: ' + error.message);
        }
    }

    async verifyEmail(email, code) {
        try {
            const response = await fetch('/api/users/register/verify', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email, verification_code: code })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Email verification failed');
            }

            return await response.json();
        } catch (error) {
            throw new Error('Email verification failed: ' + error.message);
        }
    }

    async completeRegistration(email, code, password, username, phone) {
        try {
            const response = await fetch('/api/users/register/complete', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email, verification_code: code, password, username, phone })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Registration completion failed');
            }

            return await response.json();
        } catch (error) {
            throw new Error('Registration completion failed: ' + error.message);
        }
    }


    async resendVerificationCode(email) {
        try {
            const response = await fetch('/api/users/register/resend-code', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email })
            });

            const data = await response.json();

            if (!response.ok) {
                if (response.status === 429) {
                    // Return remaining time for cooldown
                    const match = data.detail.match(/Please wait (\d+) seconds/);
                    if (match) {
                        throw new Error('COOLDOWN:' + match[1]);
                    }
                }
                throw new Error(data.detail || 'Resending verification code failed');
            }

            return data;
        } catch (error) {
            throw error;
        }
    }


    logout() {
        this.token = null;
        this.user = null;
        localStorage.removeItem('token');
        localStorage.removeItem('user');
    }

    isAuthenticated() {
        return !!this.token;
    }

    getToken() {
        return this.token;
    }

    getCurrentUser() {
        return this.user;
    }

    async updateProfile(userData) {
        try {
            const response = await authenticatedFetch('/api/users/me', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(userData)
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Profile update failed');
            }

            const updatedUser = await response.json();
            this.user = updatedUser;
            localStorage.setItem('user', JSON.stringify(updatedUser));

            return updatedUser;
        } catch (error) {
            throw new Error('Profile update failed: ' + error.message);
        }
    }

    async changePassword(currentPassword, newPassword) {
        try {
            const response = await authenticatedFetch('/api/users/change-password', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    current_password: currentPassword,
                    new_password: newPassword
                })
            });

            if (!response.ok) {
                const error = await response.json();
                throw new Error(error.detail || 'Password change failed');
            }

            return await response.json();
        } catch (error) {
            throw new Error('Password change failed: ' + error.message);
        }
    }
}

export default new AuthService();
